import React from 'react';
import { Readiness } from '../../types';
import { getReadinessDisplay } from '../drawers/review/reviewDisplay';
import css from './ReadinessMeter.module.scss';

interface ReadinessMeterProps {
  readiness: Readiness;
  /** Fixes with a decision — accepted, edited, or skipped. Skipping is a
   *  decision too, so it moves the meter just like accepting. */
  resolvedCount: number;
  totalFixes: number;
}

type MeterStage = 'start' | 'partial' | 'strong';

function getStage(
  readiness: Readiness,
  resolvedCount: number,
  totalFixes: number
): MeterStage {
  // A review that already came back strong stays "Strong" no matter how many
  // optional suggestions are pending — progress shows in the bar and the
  // microcopy, never as a downgraded label.
  if (readiness === 'strong') return 'strong';
  if (totalFixes === 0 || resolvedCount >= totalFixes) return 'strong';
  if (resolvedCount > 0) return 'partial';
  return 'start';
}

// Endpoint labels come from the shared review display map so the meter and
// the review banner never drift; "Getting stronger" is meter-only progress
// copy with no review counterpart.
const STAGE_LABELS: Record<MeterStage, string> = {
  start: getReadinessDisplay('needs_work').short,
  partial: 'Getting stronger',
  strong: getReadinessDisplay('strong').short,
};

function getMicrocopy(resolvedCount: number, totalFixes: number): string {
  if (totalFixes === 0) {
    return 'No fixes needed — your resume is ready to go';
  }
  const noun = totalFixes === 1 ? 'suggestion' : 'suggestions';
  if (resolvedCount >= totalFixes) {
    return `All ${totalFixes} ${noun} handled — nice work`;
  }
  return `${resolvedCount} of ${totalFixes} ${noun} handled`;
}

const ReadinessMeter: React.FC<ReadinessMeterProps> = ({
  readiness,
  resolvedCount,
  totalFixes,
}) => {
  const percent =
    totalFixes === 0
      ? 100
      : Math.round(
          Math.max(0, Math.min(resolvedCount / totalFixes, 1)) * 100
        );
  const stage = getStage(readiness, resolvedCount, totalFixes);

  return (
    <div className={`${css.meter} ${css[stage]}`}>
      <div className={css.headline}>
        <span className={css.label}>{STAGE_LABELS[stage]}</span>
        <span className={css.percent} aria-hidden="true">
          {percent}%
        </span>
      </div>
      <div
        className={css.track}
        role="progressbar"
        aria-label="Resume readiness"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={css.fill} style={{ width: `${percent}%` }} />
      </div>
      <p className={css.microcopy}>{getMicrocopy(resolvedCount, totalFixes)}</p>
    </div>
  );
};

export default ReadinessMeter;
